import React, { useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Card, CardHeader, CardBody, CardTitle,
  Row, Col, Form, FormGroup, Label, Input, Button, Table,
  Nav, NavItem, NavLink, TabContent, TabPane, Badge
} from "reactstrap";

import { API_BASE_URL } from "../config/api";

const BASE_URL = API_BASE_URL;

function downloadFile(data, filename) {
  const blob = new Blob([data], { type: "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

function makeUserKey(r) {
  return r.user_id || r.player_id || r.email || r.PhoneNumber || r.fullName || r.id || "unknown";
}

function aggregateByUser(rows = []) {
  const map = new Map();
  rows.forEach((r) => {
    const key = makeUserKey(r);
    if (!map.has(key)) {
      map.set(key, { row: r, count: 1 });
    } else {
      map.get(key).count += 1;
    }
  });
  return Array.from(map.values()).map((v) => ({ ...v.row, _count: v.count }));
}

function GameExports() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [filtered, setFiltered] = useState(false);
  const [winners, setWinners] = useState([]);
  const [completers, setCompleters] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [activeTab, setActiveTab] = useState("winners");
  const winnersSummary = aggregateByUser(winners);
  const completersSummary = aggregateByUser(completers);
  const participantsSummary = aggregateByUser(participants);

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("en-US", {
        timeZone: "Asia/Amman",
        year: "numeric",
        month: "numeric",
        day: "numeric"
      });
    } catch {
      return String(value);
    }
  };

  const notify = (msg, t = 5000) => {
    setStatus(msg);
    setTimeout(() => setStatus(""), t);
  };

  const fetchData = async (endpoint) => {
    const params = fromDate && toDate ? `?from=${fromDate}&to=${toDate}` : "";
    const res = await axios.get(`${BASE_URL}/game/${endpoint}${params}`);
    return res.data || [];
  };

  const prepareExportRows = (endpoint, rows) => {
    if (endpoint === "winners") {
      return rows.map((r, idx) => ({
        "#": idx + 1,
        "Full Name": r.fullName || "",
        "Email": r.email || "",
        "Phone": r.PhoneNumber || "",
        "Participation Date": formatDate(r.participation_date)
      }));
    }
    if (endpoint === "completers") {
      return rows.map((r, idx) => ({
        "#": idx + 1,
        "Full Name": r.fullName || "",
        "Email": r.email || "",
        "Phone": r.PhoneNumber || "",
        "Level": r.level || "",
        "Score": r.score ?? "",
        "Completed At": formatDate(r.completed_at)
      }));
    }
    if (endpoint === "participants") {
      return rows.map((r, idx) => ({
        "#": idx + 1,
        "Full Name": r.fullName || "",
        "Email": r.email || "",
        "Phone": r.PhoneNumber || "",
        "Level": r.level || "",
        "Status": r.status || "",
        "Start Time": formatDate(r.startTime)
      }));
    }
    return rows;
  };

  const applyFilter = async () => {
    try {
      const [w, c, p] = await Promise.all([
        fetchData("winners"),
        fetchData("completers"),
        fetchData("participants")
      ]);
      setWinners(w);
      setCompleters(c);
      setParticipants(p);
      setFiltered(true);
      notify("Filtered results loaded.");
    } catch {
      notify("Filter failed.");
    }
  };

  const exportXlsx = async (endpoint, filename) => {
    try {
      const rows = await fetchData(endpoint);
      const ws = XLSX.utils.json_to_sheet(prepareExportRows(endpoint, rows));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "data");
      const data = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      downloadFile(data, filename);
      notify("Exported XLSX.");
    } catch {
      notify("Export failed.");
    }
  };

  const exportCsv = async (endpoint, filename) => {
    try {
      const rows = await fetchData(endpoint);
      const ws = XLSX.utils.json_to_sheet(prepareExportRows(endpoint, rows));
      const csv = XLSX.utils.sheet_to_csv(ws);
      downloadFile(csv, filename);
      notify("Exported CSV.");
    } catch {
      notify("Export failed.");
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Data Exporting</CardTitle>
            </CardHeader>
            <CardBody>
              {status && <div className="alert alert-info">{status}</div>}
              <Form className="mb-3">
                <Row form>
                  <Col md="4">
                    <FormGroup>
                      <Label>From Date</Label>
                      <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>To Date</Label>
                      <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                    </FormGroup>
                  </Col>
                  <Col md="4" className="d-flex align-items-center">
                    <Button color="primary" onClick={applyFilter}>Filter</Button>
                  </Col>
                </Row>
              </Form>

              <Row>
                <Col md="4">
                  <h6>Export Winners</h6>
                  <Button color="primary" onClick={() => exportXlsx("winners", "winners.xlsx")}>Export XLSX</Button>{" "}
                  <Button color="secondary" onClick={() => exportCsv("winners", "winners.csv")}>Export CSV</Button>
                </Col>
                <Col md="4">
                  <h6>Export Game Completers</h6>
                  <Button color="primary" onClick={() => exportXlsx("completers", "completers.xlsx")}>Export XLSX</Button>{" "}
                  <Button color="secondary" onClick={() => exportCsv("completers", "completers.csv")}>Export CSV</Button>
                </Col>
                <Col md="4">
                  <h6>Export Participants</h6>
                  <Button color="primary" onClick={() => exportXlsx("participants", "participants.xlsx")}>Export XLSX</Button>{" "}
                  <Button color="secondary" onClick={() => exportCsv("participants", "participants.csv")}>Export CSV</Button>
                </Col>
              </Row>

              {filtered && (
                <div className="mt-4">
                  <Nav tabs>
                    <NavItem>
                      <NavLink
                        className={activeTab === "winners" ? "active" : ""}
                        onClick={() => setActiveTab("winners")}
                      >
                        Winners <Badge color="secondary">{winners.length}</Badge>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "completers" ? "active" : ""}
                        onClick={() => setActiveTab("completers")}
                      >
                        Completers <Badge color="secondary">{completers.length}</Badge>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "participants" ? "active" : ""}
                        onClick={() => setActiveTab("participants")}
                      >
                        Participants <Badge color="secondary">{participants.length}</Badge>
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={activeTab} className="mt-3">
                    {activeTab === "winners" && (
                      <TabPane tabId="winners">
                        <h5>Winners</h5>
                        <Table responsive>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Times</th>
                            <th>Full Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Participation Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {winnersSummary.map((r, idx) => (
                            <tr key={`${r.user_id || r.id || r.email || r.PhoneNumber}-${r._count}`}>
                              <td>{idx + 1}</td>
                              <td>{r._count || 1}</td>
                              <td>{r.fullName || "-"}</td>
                              <td>{r.email || "-"}</td>
                              <td>{r.PhoneNumber || "-"}</td>
                              <td>{formatDate(r.participation_date)}</td>
                            </tr>
                          ))}
                          {winnersSummary.length === 0 && (
                            <tr><td colSpan="6">No winners.</td></tr>
                          )}
                        </tbody>
                        </Table>
                      </TabPane>
                    )}

                    {activeTab === "completers" && (
                      <TabPane tabId="completers">
                        <h5>Game Completers</h5>
                        <Table responsive>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Times</th>
                              <th>Full Name</th>
                              <th>Email</th>
                              <th>Phone</th>
                              <th>Level</th>
                              <th>Score</th>
                              <th>Completed At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {completersSummary.map((r, idx) => (
                              <tr key={`${r.player_id || r.user_id || r.email || r.PhoneNumber}-${r._count}`}>
                                <td>{idx + 1}</td>
                                <td>{r._count || 1}</td>
                                <td>{r.fullName || "-"}</td>
                                <td>{r.email || "-"}</td>
                                <td>{r.PhoneNumber || "-"}</td>
                                <td>{r.level || "-"}</td>
                                <td>{r.score ?? "-"}</td>
                                <td>{formatDate(r.completed_at)}</td>
                              </tr>
                            ))}
                            {completersSummary.length === 0 && (
                              <tr><td colSpan="8">No completers.</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </TabPane>
                    )}

                    {activeTab === "participants" && (
                      <TabPane tabId="participants">
                        <h5>Participants</h5>
                        <Table responsive>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Times</th>
                              <th>Full Name</th>
                              <th>Email</th>
                              <th>Phone</th>
                              <th>Level</th>
                              <th>Status</th>
                              <th>Start Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {participantsSummary.map((r, idx) => (
                              <tr key={`${r.player_id || r.user_id || r.email || r.PhoneNumber}-${r._count}`}>
                                <td>{idx + 1}</td>
                                <td>{r._count || 1}</td>
                                <td>{r.fullName || "-"}</td>
                                <td>{r.email || "-"}</td>
                                <td>{r.PhoneNumber || "-"}</td>
                                <td>{r.level || "-"}</td>
                                <td>{r.status || "-"}</td>
                                <td>{formatDate(r.startTime)}</td>
                              </tr>
                            ))}
                            {participantsSummary.length === 0 && (
                              <tr><td colSpan="8">No participants.</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </TabPane>
                    )}
                  </TabContent>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default GameExports;
